import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { gql } from '../gql/gql'
import { useMutation, useQuery } from 'urql'
import { ChevronLeft, Loader2, Sparkles } from 'lucide-react'
import { Button } from '../components/ui/button'
import { useConsent } from '../context/consent-context'
import { InsightCard } from '../components/insight-card'

const CURRENT_REVIEW_QUERY = gql(`
  query CurrentSavingsReview {
    currentSavingsReview {
      id
      status
      requestedAt
      insights {
        title
        body
        annualSaving
        category
        merchant
      }
    }
  }
`)

const POLL_REVIEW_QUERY = gql(`
  query PollSavingsReview($id: ID!) {
    savingsReview(id: $id) {
      id
      status
      requestedAt
      insights {
        title
        body
        annualSaving
        category
        merchant
      }
    }
  }
`)

const TRIGGER_REVIEW_MUTATION = gql(`
  mutation TriggerSavingsReviewPage {
    triggerSavingsReview {
      id
      status
      requestedAt
      insights {
        title
        body
        annualSaving
        category
        merchant
      }
    }
  }
`)

type Insight = { title: string; body: string; annualSaving: number; category: string; merchant?: string | null }
type UIState = 'loading' | 'idle' | 'pending' | 'ready' | 'failed'

const COOLDOWN_MS = 4 * 60 * 60 * 1000

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function cooldownRemaining(iso: string): string | null {
  const diff = new Date(iso).getTime() + COOLDOWN_MS - Date.now()
  if (diff <= 0) return null
  const hrs = Math.floor(diff / 3600000)
  const mins = Math.floor((diff % 3600000) / 60000)
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`
}

export function SavingsReviewPage() {
  const navigate = useNavigate()
  const { aiConsent, grantConsent } = useConsent()
  const [reviewId, setReviewId] = useState<string | null>(null)
  const [uiState, setUiState] = useState<UIState>('loading')
  const [insights, setInsights] = useState<Insight[]>([])
  const [requestedAt, setRequestedAt] = useState<string | null>(null)

  const [, triggerReview] = useMutation(TRIGGER_REVIEW_MUTATION)

  const [{ data: currentData, fetching: checkingCurrent }] = useQuery({
    query: CURRENT_REVIEW_QUERY,
    requestPolicy: 'network-only',
  })

  const [{ data: pollData }, reexecutePoll] = useQuery({
    query: POLL_REVIEW_QUERY,
    variables: { id: reviewId ?? '' },
    pause: !reviewId || uiState !== 'pending',
    requestPolicy: 'network-only',
  })

  useEffect(() => {
    if (checkingCurrent) return
    const current = currentData?.currentSavingsReview
    if (!current) { setUiState('idle'); return }
    setRequestedAt(current.requestedAt)
    if (current.status === 'READY') {
      setInsights((current.insights ?? []) as Insight[])
      setUiState('ready')
    } else if (current.status === 'PENDING') {
      setReviewId(current.id)
      setUiState('pending')
    } else if (current.status === 'FAILED') {
      setUiState('failed')
    } else {
      setUiState('idle')
    }
  }, [currentData, checkingCurrent])

  useEffect(() => {
    if (uiState !== 'pending' || !reviewId) return
    const timer = setInterval(() => {
      reexecutePoll({ requestPolicy: 'network-only' })
    }, 3000)
    return () => clearInterval(timer)
  }, [uiState, reviewId, reexecutePoll])

  useEffect(() => {
    const review = pollData?.savingsReview
    if (!review) return
    if (review.requestedAt) setRequestedAt(review.requestedAt)
    if (review.status === 'READY') {
      setInsights((review.insights ?? []) as Insight[])
      setUiState('ready')
    } else if (review.status === 'FAILED') {
      setUiState('failed')
    }
  }, [pollData])

  async function handleTrigger() {
    setReviewId(null)
    setInsights([])
    setUiState('pending')
    const result = await triggerReview({})
    const review = result.data?.triggerSavingsReview
    if (result.error || !review) { setUiState('failed'); return }
    if (review.requestedAt) setRequestedAt(review.requestedAt)
    if (review.status === 'READY') {
      setInsights((review.insights ?? []) as Insight[])
      setUiState('ready')
    } else {
      setReviewId(review.id)
    }
  }

  const remaining = requestedAt ? cooldownRemaining(requestedAt) : null

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 sticky top-0 z-10">
        <div className="container mx-auto flex h-14 items-center gap-3 px-4 md:px-6 max-w-2xl">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Savings Review
          </h1>
          {uiState === 'ready' && (
            <div className="ml-auto">
              {remaining ? (
                <span className="text-xs text-muted-foreground">Next in {remaining}</span>
              ) : (
                <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleTrigger}>
                  Run new
                </Button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 max-w-2xl py-8">

        {uiState === 'loading' && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {uiState === 'idle' && (
          aiConsent === false ? (
            <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-amber-500" />
              </div>
              <div>
                <p className="text-lg font-semibold mb-1">AI features are off</p>
                <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                  Enable AI features to analyse your spending and surface savings opportunities.
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  Your data is used only to generate your results and is never stored. Powered by Claude (Anthropic).
                </p>
              </div>
              <Button onClick={grantConsent} className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                Enable AI Features
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-amber-500" />
              </div>
              <div>
                <p className="text-lg font-semibold mb-1">Find where your money's going</p>
                <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                  We'll look for forgotten subscriptions, duplicate services, and price differences — using only your own receipts.
                </p>
              </div>
              <Button onClick={handleTrigger} className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                Run my review
              </Button>
            </div>
          )
        )}

        {uiState === 'pending' && (
          <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <Loader2 className="h-7 w-7 text-amber-500 animate-spin" />
            </div>
            <div>
              <p className="text-lg font-semibold mb-1">Analysing your spending…</p>
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                Looking for subscriptions, duplicates, and price differences in your receipts.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Usually takes 10–20 seconds</p>
          </div>
        )}

        {uiState === 'failed' && (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <p className="text-lg font-semibold">Something went wrong</p>
            <p className="text-sm text-muted-foreground max-w-xs">
              We couldn't complete your review. Check your connection and try again.
            </p>
            <Button onClick={handleTrigger} className="mt-2 bg-amber-500 hover:bg-amber-600 text-white">
              Try again
            </Button>
          </div>
        )}

        {uiState === 'ready' && (
          <div className="space-y-4">
            {insights.length > 0 ? (
              <>
                {insights.map((ins, i) => (
                  <InsightCard key={i} insight={ins} />
                ))}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
                <span className="text-4xl">👍</span>
                <p className="text-lg font-semibold">No significant savings found</p>
                <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                  Keep scanning receipts and check back in a few months — the more data you have, the more we can find.
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground text-center pt-2">
              {requestedAt ? `Run ${timeAgo(requestedAt)} · ` : ''}Based on your last 6 months of receipts.
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
